import type {
  MemberTransactionFiltersParam,
  MemberTransactionsTableContext
} from '@library/types/members/memberTransactions';
import { useCallback, useMemo, useState } from 'react';
import type { MemberPromotionDrawerContext } from '@library/types/members/memberPromotions';
import { useGetMemberTranactionTypesQuery } from '@bo-services/members/memberTransactionsApi';
import { useMemberTransactionFilters } from '@bo-hooks/members';
import { useParams } from 'react-router-dom';

export const useMemberTransactions = () => {
  const { memberId } = useParams<{ memberId?: string }>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedPromotionId, setSelectedPromotionId] = useState<string>('');
  const [selectedEntryId, setSelectedEntryId] = useState<string>('');

  const { data: memberTransactionTypeOptions = [] } =
    useGetMemberTranactionTypesQuery();

  const {
    memberTransactionFilters,
    onChangeMemberTransactionsFilter,
    onResetMemberTransactionFilters
  } = useMemberTransactionFilters();

  const handleChangeMemberTransactionsFilter = useCallback(
    ({ key, value }: MemberTransactionFiltersParam) => {
      onChangeMemberTransactionsFilter({ key, value });
      setCurrentPage(1);
    },
    [onChangeMemberTransactionsFilter, setCurrentPage]
  );

  const handleCurrentPageChange = useCallback(() => {
    setCurrentPage((prevPage) => prevPage + 1);
  }, [setCurrentPage]);

  const handleResetMemberTransactionFilters = useCallback(() => {
    onResetMemberTransactionFilters();
    setCurrentPage(1);
  }, [onResetMemberTransactionFilters, setCurrentPage]);

  const handleClickPromotionId = useCallback(
    ({ promotionId }: { promotionId: string }) => {
      setSelectedPromotionId(promotionId);
    },
    [setSelectedPromotionId]
  );

  const handleClickEntryId = useCallback(
    ({ entryId }: { entryId: string }) => {
      setSelectedPromotionId('');
      setSelectedEntryId(entryId);
    },
    [setSelectedEntryId, setSelectedPromotionId]
  );

  const handleClosePromotionDrawer = useCallback(() => {
    setSelectedPromotionId('');
  }, [setSelectedPromotionId]);

  const handleCloseEntryDetailsModal = useCallback(() => {
    setSelectedEntryId('');
  }, [setSelectedEntryId]);

  const memberTransactionsTableContext: MemberTransactionsTableContext =
    useMemo(
      () => ({
        memberTransactionTypeOptions,
        onClickPromotionId: handleClickPromotionId,
        onClickNewWagerId: handleClickEntryId
      }),
      [memberTransactionTypeOptions, handleClickPromotionId, handleClickEntryId]
    );

  const memberPromotionDrawerContext: MemberPromotionDrawerContext = useMemo(
    () => ({
      memberId: memberId || '',
      onClickEntryId: handleClickEntryId,
      onClosePromotionsDrawer: handleClosePromotionDrawer,
      promotionId: selectedPromotionId
    }),
    [
      handleClickEntryId,
      handleClosePromotionDrawer,
      memberId,
      selectedPromotionId
    ]
  );

  return {
    currentPage,
    memberPromotionDrawerContext,
    memberTransactionFilters,
    memberTransactionTypeOptions,
    memberTransactionsTableContext,
    onChangeMemberTransactionsFilter: handleChangeMemberTransactionsFilter,
    onCloseEntryDetailsModal: handleCloseEntryDetailsModal,
    onCurrentPageChange: handleCurrentPageChange,
    onResetMemberTransactionFilters: handleResetMemberTransactionFilters,
    selectedEntryId
  };
};
